import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
// eslint-disable-next-line import/no-unresolved
import { Action, TypedAction } from '@ngrx/store/src/models';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { PermissionCheckService } from './../../../../services/permission-check.service';
import {
    fetchMobileDeviceMailboxPolicy,
    updateExoMobileDeviceMailboxPolicyFailure,
} from './../../../../stores/client/powershell/exo/mobile-device-mailbox-policy/actions';
import * as AntiPhishPolicy from './baselines/exo/AntiPhishPolicy';
import * as SafeAttachmentPolicy from './baselines/exo/SafeAttachmentPolicy';
import * as SafeLinksPolicy from './baselines/exo/SafeLinksPolicy';
import * as MailboxAuditLog from './baselines/exo/AuditDisabled';
import * as AutoForwardBlockTransportRule from './baselines/exo/AutoForwardBlockTransportRule';
import * as AutoForwarding from './baselines/exo/AutoForwardEnabled';
import * as CustomerLockbox from './baselines/exo/CustomerLockboxEnabled';
import * as DkimSigningConfig from './baselines/exo/DkimSigningConfig';
import * as HostedContentFilterPolicy from './baselines/exo/HostedContentFilterPolicy';
import * as HostedOutboundSpamFilterPolicy from './baselines/exo/HostedOutboundSpamFilterPolicy';
import * as AntiMalwarePolicy from './baselines/exo/AntiMalwarePolicy';
import * as MalwareFileTypesFilterPolicy from './baselines/exo/MalwareFileTypesFilterPolicy';
import * as MalwareInternalSenderFilterNotificationPolicy from './baselines/exo/MalwareInternalSenderFilterNotificationPolicy';
import * as MobileDeviceMailBoxPolicy from './baselines/exo/MobileDeviceMailboxPolicy';
import * as ModernAuth from './baselines/exo/OAuth2ClientProfileEnabled';
import * as PresetEOPProtectionPolicyRuleStandard from './baselines/exo/PresetEOPProtectionPolicyRuleStandard';
import * as PresetEOPProtectionPolicyRuleStrict from './baselines/exo/PresetEOPProtectionPolicyRuleStrict';
import * as SmtpAccess from './baselines/exo/SmtpClientAuthenticationDisabled';
import * as RejectDirectSend from './baselines/exo/RejectDirectSend';
import * as UnifiedAuditLog from './baselines/exo/UnifiedAuditLogIngestionEnabled';

// graph
import * as AndroidCompliancePolicy from './baselines/graph/intune/compliance-policy/AndroidCompliancePolicy';
import * as IOSCompliancePolicy from './baselines/graph/intune/compliance-policy/IOSCompliancePolicy';
import * as MacOsCompliancePolicy from './baselines/graph/intune/compliance-policy/MacOsCompliancePolicy';
import * as Windows10CompliancePolicy from './baselines/graph/intune/compliance-policy/Windows10CompliancePolicy';
import * as ConfigurationPolicyWindowsEdgeBlockPasswordSaving from './baselines/graph/intune/configuration-policy/configuration-policy-windows-edge-block-password-saving';
import * as ConfigurationPolicyWindowsChromeBlockPasswordSaving from './baselines/graph/intune/configuration-policy/configuration-policy-windows-chrome-block-password-saving';
import * as ConfigurationPolicyWindowsLAPS from './baselines/graph/intune/configuration-policy/configuration-policy-windows-laps';
import { selectCapData, selectCapStatus } from './baselines/graph/conditional-access-policy';
import * as MfaConditionalAccessPolicy from './baselines/graph/conditional-access-policy/AdminMFAConditionalAccessPolicy';
import * as CAPGeneric from './baselines/graph/conditional-access-policy/cap-generic';
import * as CAPAppEnforcedRestrictionUnmanagedDevices from './baselines/graph/conditional-access-policy/CAPAppEnforcedRestrictionUnmanagedDevices';
import * as CAPAppProtectionPolicy from './baselines/graph/conditional-access-policy/CAPAppProtectionPolicy';
import * as CAPEnforceMFA from './baselines/graph/conditional-access-policy/CAPEnforceMFA';
import * as CAPLegacyAuth from './baselines/graph/conditional-access-policy/CAPLegacyAuth';
import * as CAPNoPersistentBrowsers from './baselines/graph/conditional-access-policy/CAPNoPersistentBrowsers';
import * as CAPRequireCompliantOrHybridAzureADJoinedDevice from './baselines/graph/conditional-access-policy/CAPRequireCompliantOrHybridAzureADJoinedDevice';
import * as CAPRestrictAzureAccess from './baselines/graph/conditional-access-policy/CAPRestrictAzureAccess';
import * as CAPRestrictMicrosoftAdminPortalAccess from './baselines/graph/conditional-access-policy/CAPRestrictMicrosoftAdminPortalAccess';
import * as CAPRiskySignin from './baselines/graph/conditional-access-policy/CAPRiskySignin';
import * as CAPSecuringSecurityInfoRegistration from './baselines/graph/conditional-access-policy/CAPSecuringSecurityInfoRegistration';
import * as CAPUnSupportedDeviceBlock from './baselines/graph/conditional-access-policy/CAPUnSupportedDeviceBlock';
import * as LegacyAuthConditionalAccessPolicy from './baselines/graph/conditional-access-policy/LegacyAuthConditionalAccessPolicy';
import * as DomainPasswordPolicy from './baselines/graph/DomainPasswordPolicy';
import * as SecurityDefaultsPolicy from './baselines/graph/SecurityDefaultsPolicy';
import * as SharepointStorageWarning from './baselines/graph/SharepointStorageWarning';
import * as UserConsentWorkFlow from './baselines/graph/UserConsentWorkFlow';
import * as PasswordRuleSettings from './baselines/graph/PasswordRuleSettings';
import * as AuthenticationMethodsEmailOTP from './baselines/graph/authentication-methods/authentication-methods-email-otp';
import * as AuthenticationMethodsSMS from './baselines/graph/authentication-methods/authentication-methods-sms';
import * as AuthenticationMethodsTemporaryAccessPass from './baselines/graph/authentication-methods/authentication-methods-temporary-access-pass';
import * as AuthenticationMethodsMicrosoftAuthenticator from './baselines/graph/authentication-methods/authentication-methods-microsoft-authenticator';
import * as AuthenticationMethodsFido2 from './baselines/graph/authentication-methods/authentication-methods-fido2';
import * as AuthenticationMethodsVoice from './baselines/graph/authentication-methods/authentication-methods-voice';
import * as AuthenticationMethodsCertificateBased from './baselines/graph/authentication-methods/authentication-methods-certificate-based';
import * as CrossTenantAccessSettings from './baselines/graph/CrossTenantAccessSettings';

import * as LegacyAuthProtocolsEnabled from './baselines/spo/LegacyAuthProtocolsEnabled';
import * as PreventExternalUsersFromResharing from './baselines/spo/PreventExternalUsersFromResharing';
import * as RequireAnonymousLinksExpireInDays from './baselines/spo/RequireAnonymousLinksExpireInDays';
import * as SharepointInfectedFilesDownload from './baselines/spo/SharepointInfectedFilesDownload';
import * as AdminConsentWorkflow from './baselines/graph/admin-consent-workflow';
import { selectCompliancePolicyData } from './baselines/graph/intune/compliance-policy';
import * as OrgAnonymousLinksSharingPolicy from './baselines/spo/OrgAnonymousLinksSharingPolicy';
import { selectDeviceConfigurationPolicy } from './baselines/graph/intune/configuration-policy';
import { selectSafeAttachmentPolicy } from './baselines/exo/SafeAttachmentPolicy';
import { selectSafeLinksPolicy } from './baselines/exo/SafeLinksPolicy';
import { SERVICE_PLAN_KEY } from 'projects/angular-clarity/src/app/services/check-service-plans.service';
import { client } from 'projects/angular-clarity/src/app/stores/client';
import { loadAuthenticationStrengthPolicies } from 'projects/angular-clarity/src/app/stores/client/graph/authentication-strength-policies/actions';
import { loadAuthorizationPolicy } from 'projects/angular-clarity/src/app/stores/client/graph/authorization-policy/actions';
import { loadDirectorySettings } from 'projects/angular-clarity/src/app/stores/client/graph/directory-settings/actions';
import {
    createCompliancePolicyFailure,
    deleteCompliancePolicyFailure,
    loadCompliancePolicies,
    updateCompliancePolicyFailure,
} from 'projects/angular-clarity/src/app/stores/client/graph/intune/compliance-policies/policies/actions';
import {
    createConditionalAccessPolicyFailure,
    deleteConditionalAccessPolicyFailure,
    loadConditionalAccessPolicy,
    updateConditionalAccessPolicyFailure,
} from 'projects/angular-clarity/src/app/stores/client/graph/conditional-access/actions';
import { loadDomains, updateDomainFailure } from 'projects/angular-clarity/src/app/stores/client/graph/domains/actions';
import { loadNamedLocations } from 'projects/angular-clarity/src/app/stores/client/graph/named-locations/actions';
import {
    loadSecurityDefaultsPolicy,
    updateSecurityDefaultsFailure,
} from 'projects/angular-clarity/src/app/stores/client/graph/security-defaults/actions';
import { loadSPOSiteUsage } from 'projects/angular-clarity/src/app/stores/client/graph/spo/sharepoint-site/actions';
import {
    fetchExoAdminAuditLogConfig,
    updateExoAdminAuditLogConfigFailure,
} from 'projects/angular-clarity/src/app/stores/client/powershell/exo/admin-audit-log-config/actions';
import {
    loadAntiPhishPolicy,
    updateExoAntiPhishPolicyFailure,
} from 'projects/angular-clarity/src/app/stores/client/powershell/exo/anti-phish-policy/actions';
import {
    loadSafeAttachmentPolicy,
    updateExoSafeAttachmentPolicyFailure,
} from 'projects/angular-clarity/src/app/stores/client/powershell/exo/safe-attachment-policy/actions';
import {
    loadSafeLinksPolicy,
    updateExoSafeLinksPolicyFailure,
} from 'projects/angular-clarity/src/app/stores/client/powershell/exo/safe-links-policy/actions';
import {
    fetchDkimSigningConfig,
    updateDKIMSigningConfigFailure,
} from 'projects/angular-clarity/src/app/stores/client/powershell/exo/dkim-signing-config/actions';
import {
    disableExoEOPProtectionPolicyFailure,
    enableExoEOPProtectionPolicyFailure,
    loadEOPProtectionPolicy,
} from 'projects/angular-clarity/src/app/stores/client/powershell/exo/eop-protection-policy-rule/actions';
import {
    fetchHostedContentFilterPolicy,
    updateExoHostedContentFilterPolicyFailure,
} from 'projects/angular-clarity/src/app/stores/client/powershell/exo/hosted-content-filter-policy/actions';
import {
    fetchHostedOutboundSpamFilterPolicy,
    updateExoHostedOutboundSpamFilterPolicyFailure,
} from 'projects/angular-clarity/src/app/stores/client/powershell/exo/hosted-outbound-spam-filter-policy/actions';
import {
    loadMalwareFilterPolicy,
    updateExoMalwareFilterPolicyFailure,
} from 'projects/angular-clarity/src/app/stores/client/powershell/exo/malware-filter-policy/actions';
import {
    fetchExoOrganizationConfig,
    updateExoOrganizationConfigFailure,
} from 'projects/angular-clarity/src/app/stores/client/powershell/exo/organization-config/actions';
import {
    fetchExoRemoteDomain,
    updateExoRemoteDomainFailure,
} from 'projects/angular-clarity/src/app/stores/client/powershell/exo/remote-domain/actions';
import {
    fetchExoTransportConfig,
    updateExoTransportConfigFailure,
} from 'projects/angular-clarity/src/app/stores/client/powershell/exo/transport-config/actions';
import {
    createExoTransportRuleFailure,
    deleteExoTransportRuleFailure,
    loadExoTransportRules,
    updateExoTransportRuleFailure,
} from 'projects/angular-clarity/src/app/stores/client/powershell/exo/transport-rule/actions';
import {
    fetchSpoTenant,
    updateSpoTenantFailure,
} from 'projects/angular-clarity/src/app/stores/client/powershell/spo/tenant/actions';
import { loadDirectoryRoleTemplates } from 'projects/angular-clarity/src/app/stores/root/graph/directoryRoleTemplates/actions';
import { Status } from 'projects/angular-clarity/src/app/stores/status.interface';
import { loadAdminConsentRequestPolicy } from 'projects/angular-clarity/src/app/stores/client/graph/admin-consent-request-policy/actions';
import { fetchExoQuarantinePolicy } from 'projects/angular-clarity/src/app/stores/client/powershell/exo/quarantine-policy/actions';
import { loadGroups } from 'projects/angular-clarity/src/app/stores/client/graph/group/actions';
import { loadGraphUsers } from 'projects/angular-clarity/src/app/stores/client/graph/user/user.actions';
import { loadDeviceConfigurationPolicies } from 'projects/angular-clarity/src/app/stores/client/graph/intune/device-configuration-policies/actions';
import { loadAuthenticationMethodsPolicies } from 'projects/angular-clarity/src/app/stores/client/graph/authentication-methods-policies/actions';
import {
    loadCrossTenantAccessPolicy,
    updateCrossTenantAccessPolicyFailure,
} from 'projects/angular-clarity/src/app/stores/client/graph/cross-tenant-access-policy/actions';

const PASSWORD_RULE_SETTINGS_TEMPLATE_ID = '5cf42378-d67d-4f36-ba46-e8b86229381d';

export interface TenantBaselineRegistryItem {
    tag: string; // spec tag
    component: any;
    createRemediationAction?: (
        _tenant: string,
        data: any,
        schema: any,
        metaData?: any,
        key?: string,
    ) => TypedAction<any> | Array<TypedAction<any>> | null;
    remediationActionSuccess?: TypedAction<any> | Array<TypedAction<any>> | null;
    remediationActionFailure?: Array<TypedAction<any>> | null;

    fetch_data: Array<Action>;
    select_data: Observable<any>;
    select_status: Observable<Status>;
    meta_data?: Observable<any>;
    meta_data_status?: Observable<Status>;
    scopes?: string[];
    href?: string;
    deprecated?: boolean;
    canUpdate?: boolean; // can update if there is a template,
    allowAutoRemediation: boolean;
    plans?: Array<SERVICE_PLAN_KEY | SERVICE_PLAN_KEY[]>; // nested array for OR plans
}

@Injectable({
    providedIn: 'root',
})
export class TenantSpecRegistryService {
    unDespatchedItems: Array<TenantBaselineRegistryItem> = [];
    isInitialed = false;
    genericCapsTags = [
        'CAPGeneric1',
        'CAPGeneric2',
        'CAPGeneric3',
        'CAPGeneric4',
        'CAPGeneric5',
        'CAPGeneric6',
        'CAPGeneric7',
        'CAPGeneric8',
        'CAPGeneric9',
        'CAPGeneric10',
    ];

    genericCapsTemplateTags = [
        'CAPGenericTemplateItem1',
        'CAPGenericTemplateItem2',
        'CAPGenericTemplateItem3',
        'CAPGenericTemplateItem4',
        'CAPGenericTemplateItem5',
        'CAPGenericTemplateItem6',
        'CAPGenericTemplateItem7',
        'CAPGenericTemplateItem8',
        'CAPGenericTemplateItem9',
        'CAPGenericTemplateItem10',
    ];

    constructor(
        private store: Store<any>,
        private permissionCheck: PermissionCheckService,
    ) {}

    public init(_tenant: string): void {
        if (this.isInitialed) return;

        const reg_items = this.getAll(_tenant);
        this.dispatchItems(reg_items, _tenant);
    }

    dispatchItems(reg_items: TenantBaselineRegistryItem[], _tenant): void {
        for (const item of reg_items) {
            const scopes = item.scopes;

            const hasPermissions = this.permissionCheck.hasPermissions(_tenant, scopes);
            // don't dispatch actions if tenant doesn't have the required permission;
            hasPermissions.pipe(filter((res) => !!res)).subscribe(() => {
                item.fetch_data.forEach((action) => {
                    this.store.dispatch(action);
                });
            });
        }
    }

    public get(tenant: string, tag: string): TenantBaselineRegistryItem {
        const items = this.registry.map((fn) => fn(tenant));
        const item = items.find((item) => item.tag == tag);

        if (item === undefined) {
            // if (environment.production) {
            //     throw new Error(`no component registered for spec: ${tag}`);
            // } else {
            const item = {
                // purpose of this is not to throw error when we have components that are not registered.
                tag,
                component: 'item is not registered',
                select_data: of([]),
                allowAutoRemediation: false,
                fetch_data: [],
                select_status: of({ loading: false, loaded: true, error: false, creating: false, updating: false }),
            };

            return item;
            // }
        }

        return item;
    }

    public getAll(tenant: string): TenantBaselineRegistryItem[] {
        return this.registry.map((fn) => fn(tenant));
    }

    private registry: Array<(tid: string) => TenantBaselineRegistryItem> = [
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'OrgSMTP',
            component: SmtpAccess.BaselineComponent,
            select_data: this.store.pipe(select(client(tenant).powershell.exoTransportConfig.item)),
            fetch_data: [fetchExoTransportConfig({ _tenant: tenant })],
            select_status: this.store.pipe(select(client(tenant).powershell.exoTransportConfig.status)),
            createRemediationAction: SmtpAccess.Remediate,
            remediationActionFailure: [updateExoTransportConfigFailure],
            href: SmtpAccess.href,
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'HostedContentFilterPolicy',
            component: HostedContentFilterPolicy.BaselineComponent,
            fetch_data: [fetchHostedContentFilterPolicy({ _tenant: tenant })],
            select_data: this.store.pipe(
                select(client(tenant).powershell.hostedContentFilterPolicy.all),
                map((res) => res.filter((p) => p.Name === 'Default')),
            ),
            canUpdate: true,
            select_status: this.store.pipe(select(client(tenant).powershell.hostedContentFilterPolicy.status)),
            createRemediationAction: HostedContentFilterPolicy.Remediate,
            remediationActionFailure: [updateExoHostedContentFilterPolicyFailure],
            href: HostedContentFilterPolicy.href,
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'HostedOutboundSpamFilterPolicy',
            component: HostedOutboundSpamFilterPolicy.BaselineComponent,
            fetch_data: [fetchHostedOutboundSpamFilterPolicy({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).powershell.hostedOutboundSpamFilterPolicy.all)),
            canUpdate: true,
            select_status: this.store.pipe(select(client(tenant).powershell.hostedOutboundSpamFilterPolicy.status)),
            createRemediationAction: HostedOutboundSpamFilterPolicy.Remediate,
            remediationActionFailure: [updateExoHostedOutboundSpamFilterPolicyFailure],
            href: HostedOutboundSpamFilterPolicy.href,
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'MobileDeviceMailboxPolicy',
            component: MobileDeviceMailBoxPolicy.BaselineComponent,
            fetch_data: [fetchMobileDeviceMailboxPolicy({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).powershell.mobileDeviceMailboxPolicy.all)),
            select_status: this.store.pipe(select(client(tenant).powershell.mobileDeviceMailboxPolicy.status)),
            createRemediationAction: MobileDeviceMailBoxPolicy.Remediate,
            remediationActionFailure: [updateExoMobileDeviceMailboxPolicyFailure],
            href: MobileDeviceMailBoxPolicy.href,
            allowAutoRemediation: false,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'AntiMalwarePolicy',
            component: AntiMalwarePolicy.BaselineComponent,
            fetch_data: [loadMalwareFilterPolicy({ _tenant: tenant })],
            select_data: this.store.pipe(
                select(client(tenant).powershell.exoMalwareFilterPolicy.all),
                map((res) =>
                    res
                        .filter((res) => res.IsDefault)
                        .map((res) => ({
                            ...res,
                            FileTypes: res.FileTypes?.map((t) => t.toLocaleLowerCase()) || [],
                            InternalSenderAdminAddress: res.InternalSenderAdminAddress?.toLocaleLowerCase(),
                        })),
                ),
            ),
            canUpdate: true,
            select_status: this.store.pipe(select(client(tenant).powershell.exoMalwareFilterPolicy.status)),
            createRemediationAction: AntiMalwarePolicy.Remediate,
            remediationActionFailure: [updateExoMalwareFilterPolicyFailure],
            href: AntiMalwarePolicy.href,
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'MalwareFileTypesFilterPolicy',
            component: MalwareFileTypesFilterPolicy.BaselineComponent,
            fetch_data: [loadMalwareFilterPolicy({ _tenant: tenant })],
            select_data: this.store.pipe(
                select(client(tenant).powershell.exoMalwareFilterPolicy.all),
                map((res) =>
                    res
                        .filter((res) => res.IsDefault)
                        .map((res) => ({ ...res, FileTypes: res.FileTypes.map((res) => res.toLocaleLowerCase()) })),
                ),
            ),
            deprecated: true,
            select_status: this.store.pipe(select(client(tenant).powershell.exoMalwareFilterPolicy.status)),
            createRemediationAction: MalwareFileTypesFilterPolicy.Remediate,
            remediationActionFailure: [updateExoMalwareFilterPolicyFailure],
            href: MalwareFileTypesFilterPolicy.href,
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'MalwareInternalSenderFilterNotificationPolicy',
            component: MalwareInternalSenderFilterNotificationPolicy.BaselineComponent,
            deprecated: true,
            fetch_data: [loadMalwareFilterPolicy({ _tenant: tenant })],
            select_data: this.store.pipe(
                select(client(tenant).powershell.exoMalwareFilterPolicy.all),
                // make it case insensitive as ajv doesn't direct support case insensitive we can use pattern in ajv but it is complicated
                map((res) =>
                    res.map((item) => ({
                        ...item,
                        InternalSenderAdminAddress: item.InternalSenderAdminAddress?.toLocaleLowerCase(),
                    })),
                ),
            ),
            canUpdate: true,
            select_status: this.store.pipe(select(client(tenant).powershell.exoMalwareFilterPolicy.status)),
            createRemediationAction: MalwareInternalSenderFilterNotificationPolicy.Remediate,
            remediationActionFailure: [updateExoMalwareFilterPolicyFailure],
            href: MalwareInternalSenderFilterNotificationPolicy.href,
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'PresetEOPProtectionPolicyRuleStrict',
            component: PresetEOPProtectionPolicyRuleStrict.BaselineComponent,
            fetch_data: [loadEOPProtectionPolicy({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).powershell.exoEOPProtectionPolicyRule.all)),
            select_status: this.store.pipe(select(client(tenant).powershell.exoEOPProtectionPolicyRule.status)),
            createRemediationAction: PresetEOPProtectionPolicyRuleStrict.Remediate,
            remediationActionFailure: [enableExoEOPProtectionPolicyFailure, disableExoEOPProtectionPolicyFailure],
            href: PresetEOPProtectionPolicyRuleStrict.href,
            allowAutoRemediation: false,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'PresetEOPProtectionPolicyRuleStandard',
            component: PresetEOPProtectionPolicyRuleStandard.BaselineComponent,
            fetch_data: [loadEOPProtectionPolicy({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).powershell.exoEOPProtectionPolicyRule.all)),
            select_status: this.store.pipe(select(client(tenant).powershell.exoEOPProtectionPolicyRule.status)),
            createRemediationAction: PresetEOPProtectionPolicyRuleStandard.Remediate,
            remediationActionFailure: [enableExoEOPProtectionPolicyFailure, disableExoEOPProtectionPolicyFailure],
            href: PresetEOPProtectionPolicyRuleStandard.href,
            allowAutoRemediation: false,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'AntiPhishPolicy',
            component: AntiPhishPolicy.BaselineComponent,
            fetch_data: [loadAntiPhishPolicy({ _tenant: tenant }), fetchExoQuarantinePolicy({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).powershell.exoAntiPhishPolicy.all)),
            select_status: this.store.pipe(select(client(tenant).powershell.exoAntiPhishPolicy.status)),
            createRemediationAction: AntiPhishPolicy.Remediate,
            remediationActionFailure: [updateExoAntiPhishPolicyFailure],
            href: AntiPhishPolicy.href,
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'SafeAttachmentPolicy',
            component: SafeAttachmentPolicy.BaselineComponent,
            fetch_data: [
                loadSafeAttachmentPolicy({ _tenant: tenant }),
                loadDomains({ _tenant: tenant }),
                loadGroups({ _tenant: tenant }),
                loadGraphUsers({ _tenant: tenant }),
                fetchExoQuarantinePolicy({ _tenant: tenant }),
            ],
            select_data: selectSafeAttachmentPolicy(this.store, tenant),
            select_status: this.store.pipe(select(client(tenant).powershell.safeAttachmentPolicy.status)),
            createRemediationAction: SafeAttachmentPolicy.Remediate,
            remediationActionFailure: [updateExoSafeAttachmentPolicyFailure],
            href: SafeAttachmentPolicy.href,
            plans: [['f20fedf3-f3c3-43c3-8267-2bfdd51c0939', '8e0c0a52-6a6c-4d40-8370-dd62790dcd70']],
            canUpdate: true,
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'SafeLinksPolicy',
            component: SafeLinksPolicy.BaselineComponent,
            fetch_data: [
                loadSafeLinksPolicy({ _tenant: tenant }),
                loadDomains({ _tenant: tenant }),
                loadGroups({ _tenant: tenant }),
                loadGraphUsers({ _tenant: tenant }),
            ],
            select_data: selectSafeLinksPolicy(this.store, tenant),
            select_status: this.store.pipe(select(client(tenant).powershell.safeLinksPolicy.status)),
            createRemediationAction: SafeLinksPolicy.Remediate,
            remediationActionFailure: [updateExoSafeLinksPolicyFailure],
            href: SafeLinksPolicy.href,
            canUpdate: true,
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'OrgAutoForwarding',
            component: AutoForwarding.BaselineComponent,
            fetch_data: [fetchExoRemoteDomain({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).powershell.exoRemoteDomain.item)),
            select_status: this.store.pipe(select(client(tenant).powershell.exoRemoteDomain.status)),
            createRemediationAction: AutoForwarding.Remediate,
            remediationActionFailure: [updateExoRemoteDomainFailure],
            href: AutoForwarding.href,
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'OrgCustomerLockbox',
            component: CustomerLockbox.BaselineComponent,
            fetch_data: [fetchExoOrganizationConfig({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).powershell.exoOrganizationConfig.item)),
            select_status: this.store.pipe(select(client(tenant).powershell.exoOrganizationConfig.status)),
            createRemediationAction: CustomerLockbox.Remediate,
            remediationActionFailure: [updateExoOrganizationConfigFailure],
            href: CustomerLockbox.href,
            plans: [['9f431833-0334-42de-a7dc-70aa40db46db']],
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'OrgModernAuth',
            component: ModernAuth.BaselineComponent,
            fetch_data: [fetchExoOrganizationConfig({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).powershell.exoOrganizationConfig.item)),
            select_status: this.store.pipe(select(client(tenant).powershell.exoOrganizationConfig.status)),
            createRemediationAction: ModernAuth.Remediate,
            remediationActionFailure: [updateExoOrganizationConfigFailure],
            href: ModernAuth.href,
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'OrgMailboxAuditLog',
            component: MailboxAuditLog.BaselineComponent,
            fetch_data: [fetchExoOrganizationConfig({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).powershell.exoOrganizationConfig.item)),
            select_status: this.store.pipe(select(client(tenant).powershell.exoOrganizationConfig.status)),
            createRemediationAction: MailboxAuditLog.Remediate,
            remediationActionFailure: [updateExoOrganizationConfigFailure],
            href: MailboxAuditLog.href,
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'OrgDirectSend',
            component: RejectDirectSend.BaselineComponent,
            fetch_data: [fetchExoOrganizationConfig({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).powershell.exoOrganizationConfig.item)),
            select_status: this.store.pipe(select(client(tenant).powershell.exoOrganizationConfig.status)),
            createRemediationAction: RejectDirectSend.Remediate,
            remediationActionFailure: [updateExoOrganizationConfigFailure],
            href: RejectDirectSend.href,
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'DkimSigningConfig',
            component: DkimSigningConfig.BaselineComponent,
            fetch_data: [fetchDkimSigningConfig({ _tenant: tenant }), loadDomains({ _tenant: tenant })],
            select_data: combineLatest([
                this.store.pipe(select(client(tenant).powershell.exoDkimSigningConfig.item)),
                this.store.pipe(
                    select(client(tenant).graph.domains.all),
                    map((domains) => domains.filter((domain) => !!domain.isVerified).find((res) => res.isDefault)),
                ),
            ]).pipe(
                map(([dkimConfig, defaultDomain]) => {
                    if (!defaultDomain) {
                        return [];
                    } else {
                        const dkimSettingForDefaultDomain = dkimConfig.filter(
                            (res) => res.Id?.toLocaleLowerCase() === defaultDomain.id?.toLocaleLowerCase(),
                        );
                        return dkimSettingForDefaultDomain.map((item) => ({ ...item, IsDefault: true }));
                    }
                }),
            ),
            select_status: combineLatest([
                this.store.pipe(select(client(tenant).powershell.exoDkimSigningConfig.status)),
                this.store.pipe(select(client(tenant).graph.domains.status)),
            ]).pipe(
                map(([dkimStatus, domain_status]) => ({
                    ...dkimStatus,
                    loaded: domain_status.loaded && dkimStatus.loaded,
                })),
            ),
            meta_data: this.store.pipe(
                select(client(tenant).graph.domains.all),
                map((domains) => domains.filter((domain) => !!domain.isVerified).find((res) => res.isDefault)),
            ),
            meta_data_status: this.store.pipe(select(client(tenant).graph.domains.status)),
            createRemediationAction: DkimSigningConfig.Remediate,
            remediationActionFailure: [updateDKIMSigningConfigFailure],
            href: DkimSigningConfig.href,
            allowAutoRemediation: false,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'OrgUnifiedAuditLog',
            component: UnifiedAuditLog.BaselineComponent,
            fetch_data: [fetchExoAdminAuditLogConfig({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).powershell.exoAdminAuditLogConfig.item)),
            select_status: this.store.pipe(select(client(tenant).powershell.exoAdminAuditLogConfig.status)),
            allowAutoRemediation: false,
            createRemediationAction: UnifiedAuditLog.Remediate,
            remediationActionFailure: [updateExoAdminAuditLogConfigFailure],
            href: UnifiedAuditLog.href,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'OrgExternalResharing',
            component: PreventExternalUsersFromResharing.BaselineComponent,
            fetch_data: [fetchSpoTenant({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).powershell.spoTenant.item)),
            select_status: this.store.pipe(select(client(tenant).powershell.spoTenant.status)),
            createRemediationAction: PreventExternalUsersFromResharing.Remediate,
            remediationActionFailure: [updateSpoTenantFailure],
            href: PreventExternalUsersFromResharing.href,
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'SharepointInfectedFilesDownload',
            component: SharepointInfectedFilesDownload.BaselineComponent,
            fetch_data: [fetchSpoTenant({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).powershell.spoTenant.item)),
            select_status: this.store.pipe(select(client(tenant).powershell.spoTenant.status)),
            createRemediationAction: SharepointInfectedFilesDownload.Remediate,
            remediationActionFailure: [updateSpoTenantFailure],
            href: SharepointInfectedFilesDownload.href,
            plans: [['f20fedf3-f3c3-43c3-8267-2bfdd51c0939', '8e0c0a52-6a6c-4d40-8370-dd62790dcd70']],
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'OrgLinksExpiry',
            component: RequireAnonymousLinksExpireInDays.BaselineComponent,
            fetch_data: [fetchSpoTenant({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).powershell.spoTenant.item)),
            select_status: this.store.pipe(select(client(tenant).powershell.spoTenant.status)),
            createRemediationAction: RequireAnonymousLinksExpireInDays.Remediate,
            remediationActionFailure: [updateSpoTenantFailure],
            href: RequireAnonymousLinksExpireInDays.href,
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'OrgAnonymousLinksSharingPolicy',
            component: OrgAnonymousLinksSharingPolicy.BaselineComponent,
            fetch_data: [fetchSpoTenant({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).powershell.spoTenant.item)),
            select_status: this.store.pipe(select(client(tenant).powershell.spoTenant.status)),
            createRemediationAction: OrgAnonymousLinksSharingPolicy.Remediate,
            remediationActionFailure: [updateSpoTenantFailure],
            href: OrgAnonymousLinksSharingPolicy.href,
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'LegacyAuthProtocolsEnabled',
            component: LegacyAuthProtocolsEnabled.BaselineComponent,
            fetch_data: [fetchSpoTenant({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).powershell.spoTenant.item)),
            select_status: this.store.pipe(select(client(tenant).powershell.spoTenant.status)),
            createRemediationAction: LegacyAuthProtocolsEnabled.Remediate,
            remediationActionFailure: [updateSpoTenantFailure],
            href: LegacyAuthProtocolsEnabled.href,
            allowAutoRemediation: true,
        }),
        // // graph
        // // Authentication Methods
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'AuthenticationMethodsFido2',
            component: AuthenticationMethodsFido2.BaselineComponent,
            select_data: this.store.select(client(tenant).graph.AuthenticationMethodsPolicies.fido2),
            select_status: this.store.select(client(tenant).graph.AuthenticationMethodsPolicies.status),
            fetch_data: [loadAuthenticationMethodsPolicies({ _tenant: tenant })],
            allowAutoRemediation: true,
            createRemediationAction: AuthenticationMethodsFido2.remediate,
            href: AuthenticationMethodsFido2.href,
            canUpdate: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'AuthenticationMethodsEmailOTP',
            component: AuthenticationMethodsEmailOTP.BaselineComponent,
            select_data: this.store.select(client(tenant).graph.AuthenticationMethodsPolicies.email),
            select_status: this.store.select(client(tenant).graph.AuthenticationMethodsPolicies.status),
            fetch_data: [loadAuthenticationMethodsPolicies({ _tenant: tenant })],
            // scopes: ['Policy.ReadWrite.Authorization'],
            allowAutoRemediation: true,
            createRemediationAction: AuthenticationMethodsEmailOTP.remediate,
            href: AuthenticationMethodsEmailOTP.href,
            canUpdate: true,
        }),
        // // SMS
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'AuthenticationMethodsSMS',
            component: AuthenticationMethodsSMS.BaselineComponent,
            select_data: this.store.select(client(tenant).graph.AuthenticationMethodsPolicies.sms),
            select_status: this.store.select(client(tenant).graph.AuthenticationMethodsPolicies.status),
            fetch_data: [loadAuthenticationMethodsPolicies({ _tenant: tenant })],
            // scopes: ['Policy.ReadWrite.Authorization'],
            allowAutoRemediation: true,
            createRemediationAction: AuthenticationMethodsSMS.remediate,
            href: AuthenticationMethodsSMS.href,
            canUpdate: true,
        }),
        // // Temporary Access Pass
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'AuthenticationMethodsTemporaryAccessPass',
            component: AuthenticationMethodsTemporaryAccessPass.BaselineComponent,
            select_data: this.store.select(client(tenant).graph.AuthenticationMethodsPolicies.temporaryAccessPass),
            select_status: this.store.select(client(tenant).graph.AuthenticationMethodsPolicies.status),
            fetch_data: [loadAuthenticationMethodsPolicies({ _tenant: tenant })],
            // scopes: ['Policy.ReadWrite.Authorization'],
            allowAutoRemediation: true,
            createRemediationAction: AuthenticationMethodsTemporaryAccessPass.remediate,
            href: AuthenticationMethodsTemporaryAccessPass.href,
            canUpdate: true,
        }),
        // // Voice
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'AuthenticationMethodsVoice',
            component: AuthenticationMethodsVoice.BaselineComponent,
            select_data: this.store.select(client(tenant).graph.AuthenticationMethodsPolicies.voice),
            select_status: this.store.select(client(tenant).graph.AuthenticationMethodsPolicies.status),
            fetch_data: [loadAuthenticationMethodsPolicies({ _tenant: tenant })],
            allowAutoRemediation: true,
            createRemediationAction: AuthenticationMethodsVoice.remediate,
            href: AuthenticationMethodsVoice.href,
            canUpdate: true,
        }),
        // // Certificate-Based
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'AuthenticationMethodsCertificateBased',
            component: AuthenticationMethodsCertificateBased.BaselineComponent,
            select_data: this.store.select(client(tenant).graph.AuthenticationMethodsPolicies.x509Certificate),
            select_status: this.store.select(client(tenant).graph.AuthenticationMethodsPolicies.status),
            fetch_data: [loadAuthenticationMethodsPolicies({ _tenant: tenant })],
            allowAutoRemediation: true,
            createRemediationAction: AuthenticationMethodsCertificateBased.remediate,
            href: AuthenticationMethodsCertificateBased.href,
            canUpdate: true,
        }),
        // // Cross-Tenant Access Settings
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'CrossTenantAccessSettings',
            component: CrossTenantAccessSettings.BaselineComponent,
            select_data: this.store.select(client(tenant).graph.crossTenantAccessPolicy.data),
            select_status: this.store.select(client(tenant).graph.crossTenantAccessPolicy.status),
            fetch_data: [loadCrossTenantAccessPolicy({ _tenant: tenant })],
            allowAutoRemediation: true,
            createRemediationAction: CrossTenantAccessSettings.Remediate,
            remediationActionFailure: [updateCrossTenantAccessPolicyFailure],
            href: CrossTenantAccessSettings.href,
            canUpdate: true,
            scopes: ['Policy.Read.All'],
        }),
        // // Microsoft Authenticator
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'AuthenticationMethodsMicrosoftAuthenticator',
            component: AuthenticationMethodsMicrosoftAuthenticator.BaselineComponent,
            select_data: this.store.select(client(tenant).graph.AuthenticationMethodsPolicies.microsoftAuthenticator),
            select_status: this.store.select(client(tenant).graph.AuthenticationMethodsPolicies.status),
            fetch_data: [loadAuthenticationMethodsPolicies({ _tenant: tenant })],
            // scopes: ['Policy.ReadWrite.Authorization'],
            allowAutoRemediation: true,
            createRemediationAction: AuthenticationMethodsMicrosoftAuthenticator.Remediate,
            href: AuthenticationMethodsMicrosoftAuthenticator.href,
            canUpdate: true,
        }),
        // //
        (tenant: string): TenantBaselineRegistryItem => ({
            // Personally-owned work profile
            tag: 'UserConsentWorkFlow',
            component: UserConsentWorkFlow.BaselineComponent,
            select_data: this.store.select(client(tenant).graph.authorizationPolicy.data),
            select_status: this.store.select(client(tenant).graph.authorizationPolicy.status),
            fetch_data: [loadAuthorizationPolicy({ _tenant: tenant })],
            // scopes: ['Policy.ReadWrite.Authorization'],
            allowAutoRemediation: true,
            createRemediationAction: UserConsentWorkFlow.Remediate,
            href: UserConsentWorkFlow.href,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'PasswordRuleSettings',
            component: PasswordRuleSettings.BaselineComponent,
            fetch_data: [loadDirectorySettings({ _tenant: tenant })],
            select_data: this.store.pipe(
                select(client(tenant).graph.directorySettings.all),
                map((settings) => settings.find((s) => s.templateId === PASSWORD_RULE_SETTINGS_TEMPLATE_ID)),
            ),
            select_status: this.store.select(client(tenant).graph.directorySettings.status),
            createRemediationAction: PasswordRuleSettings.Remediate,
            scopes: ['Directory.ReadWrite.All'],
            href: PasswordRuleSettings.href,
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'DomainPasswordPolicy',
            component: DomainPasswordPolicy.BaselineComponent,
            fetch_data: [loadDomains({ _tenant: tenant })],
            select_data: this.store.pipe(
                select(client(tenant).graph.domains.all),
                map((domains) => domains.filter((domain) => !!domain.isVerified && !!domain.isRoot)),
            ),
            select_status: this.store.select(client(tenant).graph.domains.status),
            createRemediationAction: DomainPasswordPolicy.Remediate,
            remediationActionFailure: [updateDomainFailure],
            scopes: ['Domain.ReadWrite.All', 'Directory.Read.All'],
            href: DomainPasswordPolicy.href,
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'MfaConditionalAccessPolicy',
            component: MfaConditionalAccessPolicy.BaselineComponent,
            fetch_data: [loadConditionalAccessPolicy({ _tenant: tenant }), loadDirectoryRoleTemplates({ msp: tenant })],
            select_data: this.store.pipe(select(client(tenant).graph.conditionalAccessPolicy.all)),
            select_status: this.store.select(client(tenant).graph.conditionalAccessPolicy.status),
            meta_data: this.store.pipe(select(client(tenant).graph.users.all)),
            canUpdate: true,
            deprecated: true,
            meta_data_status: this.store.select(client(tenant).graph.users.status),
            createRemediationAction: MfaConditionalAccessPolicy.Remediate,
            remediationActionFailure: [
                createConditionalAccessPolicyFailure,
                updateConditionalAccessPolicyFailure,
                deleteConditionalAccessPolicyFailure,
            ],
            scopes: ['Policy.Read.All', 'Policy.ReadWrite.ConditionalAccess', 'Application.Read.All'],
            href: MfaConditionalAccessPolicy.href,
            plans: [['41781fb2-bc02-4b7c-bd55-b576c07bb09d', 'eec0eb4f-6444-4f95-aba0-50c24d67f998']],
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'CAPEnforceMFA',
            component: CAPEnforceMFA.BaselineComponent,
            fetch_data: [
                loadAuthenticationStrengthPolicies({ _tenant: tenant }),
                loadConditionalAccessPolicy({ _tenant: tenant }),
                loadDirectoryRoleTemplates({ msp: tenant }),
                loadNamedLocations({ _tenant: tenant }),
            ],
            select_data: this.store.pipe(select(client(tenant).graph.conditionalAccessPolicy.all)),
            select_status: this.store.select(client(tenant).graph.conditionalAccessPolicy.status),
            meta_data: this.store.pipe(select(client(tenant).graph.users.all)),
            canUpdate: true,
            meta_data_status: this.store.select(client(tenant).graph.users.status),
            createRemediationAction: CAPEnforceMFA.Remediate,
            remediationActionFailure: [
                createConditionalAccessPolicyFailure,
                updateConditionalAccessPolicyFailure,
                deleteConditionalAccessPolicyFailure,
            ],
            scopes: ['Policy.Read.All', 'Policy.ReadWrite.ConditionalAccess', 'Application.Read.All'],
            href: CAPEnforceMFA.href,
            plans: [['41781fb2-bc02-4b7c-bd55-b576c07bb09d', 'eec0eb4f-6444-4f95-aba0-50c24d67f998']],
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'CAPNoPersistentBrowsers',
            component: CAPNoPersistentBrowsers.BaselineComponent,
            canUpdate: true,
            fetch_data: [loadConditionalAccessPolicy({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).graph.conditionalAccessPolicy.all)),
            select_status: this.store.select(client(tenant).graph.conditionalAccessPolicy.status),
            createRemediationAction: CAPNoPersistentBrowsers.Remediate,
            remediationActionFailure: [
                createConditionalAccessPolicyFailure,
                updateConditionalAccessPolicyFailure,
                deleteConditionalAccessPolicyFailure,
            ],
            scopes: ['Policy.Read.All', 'Policy.ReadWrite.ConditionalAccess', 'Application.Read.All'],
            href: CAPNoPersistentBrowsers.href,
            plans: [['41781fb2-bc02-4b7c-bd55-b576c07bb09d', 'eec0eb4f-6444-4f95-aba0-50c24d67f998']],
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'CAPUnSupportedDeviceBlock',
            component: CAPUnSupportedDeviceBlock.BaselineComponent,
            canUpdate: true,
            fetch_data: [loadConditionalAccessPolicy({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).graph.conditionalAccessPolicy.all)),
            select_status: this.store.select(client(tenant).graph.conditionalAccessPolicy.status),
            createRemediationAction: CAPUnSupportedDeviceBlock.Remediate,
            remediationActionFailure: [
                createConditionalAccessPolicyFailure,
                updateConditionalAccessPolicyFailure,
                deleteConditionalAccessPolicyFailure,
            ],
            scopes: ['Policy.Read.All', 'Policy.ReadWrite.ConditionalAccess', 'Application.Read.All'],
            href: CAPUnSupportedDeviceBlock.href,
            plans: [['41781fb2-bc02-4b7c-bd55-b576c07bb09d', 'eec0eb4f-6444-4f95-aba0-50c24d67f998']],
            allowAutoRemediation: false,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'CAPRestrictAzureAccess',
            component: CAPRestrictAzureAccess.BaselineComponent,
            canUpdate: true,
            fetch_data: [loadConditionalAccessPolicy({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).graph.conditionalAccessPolicy.all)),
            select_status: this.store.select(client(tenant).graph.conditionalAccessPolicy.status),
            createRemediationAction: CAPRestrictAzureAccess.Remediate,
            remediationActionFailure: [
                createConditionalAccessPolicyFailure,
                updateConditionalAccessPolicyFailure,
                deleteConditionalAccessPolicyFailure,
            ],
            scopes: ['Policy.Read.All', 'Policy.ReadWrite.ConditionalAccess', 'Application.Read.All'],
            href: CAPRestrictAzureAccess.href,
            plans: [['41781fb2-bc02-4b7c-bd55-b576c07bb09d', 'eec0eb4f-6444-4f95-aba0-50c24d67f998']],
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'CAPRestrictMicrosoftAdminPortalAccess',
            component: CAPRestrictMicrosoftAdminPortalAccess.BaselineComponent,
            canUpdate: true,
            fetch_data: [loadConditionalAccessPolicy({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).graph.conditionalAccessPolicy.all)),
            select_status: this.store.select(client(tenant).graph.conditionalAccessPolicy.status),
            createRemediationAction: CAPRestrictMicrosoftAdminPortalAccess.Remediate,
            remediationActionFailure: [
                createConditionalAccessPolicyFailure,
                updateConditionalAccessPolicyFailure,
                deleteConditionalAccessPolicyFailure,
            ],
            scopes: ['Policy.Read.All', 'Policy.ReadWrite.ConditionalAccess', 'Application.Read.All'],
            href: CAPRestrictMicrosoftAdminPortalAccess.href,
            plans: [['41781fb2-bc02-4b7c-bd55-b576c07bb09d', 'eec0eb4f-6444-4f95-aba0-50c24d67f998']],
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'CAPAppProtectionPolicy',
            component: CAPAppProtectionPolicy.BaselineComponent,
            canUpdate: true,
            fetch_data: [loadConditionalAccessPolicy({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).graph.conditionalAccessPolicy.all)),
            select_status: this.store.select(client(tenant).graph.conditionalAccessPolicy.status),
            createRemediationAction: CAPAppProtectionPolicy.Remediate,
            remediationActionFailure: [
                createConditionalAccessPolicyFailure,
                updateConditionalAccessPolicyFailure,
                deleteConditionalAccessPolicyFailure,
            ],
            scopes: ['Policy.Read.All', 'Policy.ReadWrite.ConditionalAccess', 'Application.Read.All'],
            href: CAPAppProtectionPolicy.href,
            plans: [['41781fb2-bc02-4b7c-bd55-b576c07bb09d', 'eec0eb4f-6444-4f95-aba0-50c24d67f998']],
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'CAPSecuringSecurityInfoRegistration',
            component: CAPSecuringSecurityInfoRegistration.BaselineComponent,
            canUpdate: true,
            fetch_data: [loadConditionalAccessPolicy({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).graph.conditionalAccessPolicy.all)),
            select_status: this.store.select(client(tenant).graph.conditionalAccessPolicy.status),
            createRemediationAction: CAPSecuringSecurityInfoRegistration.Remediate,
            remediationActionFailure: [
                createConditionalAccessPolicyFailure,
                updateConditionalAccessPolicyFailure,
                deleteConditionalAccessPolicyFailure,
            ],
            scopes: ['Policy.Read.All', 'Policy.ReadWrite.ConditionalAccess', 'Application.Read.All'],
            href: CAPSecuringSecurityInfoRegistration.href,
            plans: [['41781fb2-bc02-4b7c-bd55-b576c07bb09d', 'eec0eb4f-6444-4f95-aba0-50c24d67f998']],
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'CAPAppEnforcedRestrictionUnmanagedDevices',
            component: CAPAppEnforcedRestrictionUnmanagedDevices.BaselineComponent,
            canUpdate: true,
            fetch_data: [loadConditionalAccessPolicy({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).graph.conditionalAccessPolicy.all)),
            select_status: this.store.select(client(tenant).graph.conditionalAccessPolicy.status),
            createRemediationAction: CAPAppEnforcedRestrictionUnmanagedDevices.Remediate,
            remediationActionFailure: [
                createConditionalAccessPolicyFailure,
                updateConditionalAccessPolicyFailure,
                deleteConditionalAccessPolicyFailure,
            ],
            scopes: ['Policy.Read.All', 'Policy.ReadWrite.ConditionalAccess', 'Application.Read.All'],
            href: CAPAppEnforcedRestrictionUnmanagedDevices.href,
            plans: [['41781fb2-bc02-4b7c-bd55-b576c07bb09d', 'eec0eb4f-6444-4f95-aba0-50c24d67f998']],
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'CAPRequireCompliantOrHybridAzureADJoinedDevice',
            component: CAPRequireCompliantOrHybridAzureADJoinedDevice.BaselineComponent,
            canUpdate: true,
            fetch_data: [loadConditionalAccessPolicy({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).graph.conditionalAccessPolicy.all)),
            select_status: this.store.select(client(tenant).graph.conditionalAccessPolicy.status),
            createRemediationAction: CAPRequireCompliantOrHybridAzureADJoinedDevice.Remediate,
            remediationActionFailure: [
                createConditionalAccessPolicyFailure,
                updateConditionalAccessPolicyFailure,
                deleteConditionalAccessPolicyFailure,
            ],
            scopes: ['Policy.Read.All', 'Policy.ReadWrite.ConditionalAccess', 'Application.Read.All'],
            href: CAPRequireCompliantOrHybridAzureADJoinedDevice.href,
            plans: [['41781fb2-bc02-4b7c-bd55-b576c07bb09d', 'eec0eb4f-6444-4f95-aba0-50c24d67f998']],
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'CAPRiskySignin',
            component: CAPRiskySignin.BaselineComponent,
            canUpdate: true,
            fetch_data: [loadConditionalAccessPolicy({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).graph.conditionalAccessPolicy.all)),
            select_status: this.store.select(client(tenant).graph.conditionalAccessPolicy.status),
            scopes: ['Policy.Read.All', 'Policy.ReadWrite.ConditionalAccess', 'Application.Read.All'],
            plans: [['41781fb2-bc02-4b7c-bd55-b576c07bb09d', 'eec0eb4f-6444-4f95-aba0-50c24d67f998']],
            createRemediationAction: CAPRiskySignin.Remediate,
            remediationActionFailure: [
                createConditionalAccessPolicyFailure,
                updateConditionalAccessPolicyFailure,
                deleteConditionalAccessPolicyFailure,
            ],
            href: CAPRiskySignin.href,
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'LegacyAuthConditionalAccessPolicy',
            component: LegacyAuthConditionalAccessPolicy.BaselineComponent,
            fetch_data: [loadConditionalAccessPolicy({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).graph.conditionalAccessPolicy.all)),
            select_status: this.store.pipe(select(client(tenant).graph.conditionalAccessPolicy.status)),
            canUpdate: true,
            deprecated: true,
            createRemediationAction: LegacyAuthConditionalAccessPolicy.Remediate,
            remediationActionFailure: [
                createConditionalAccessPolicyFailure,
                updateConditionalAccessPolicyFailure,
                deleteConditionalAccessPolicyFailure,
            ],
            scopes: ['Policy.Read.All', 'Policy.ReadWrite.ConditionalAccess', 'Application.Read.All'],
            href: LegacyAuthConditionalAccessPolicy.href,
            plans: [['41781fb2-bc02-4b7c-bd55-b576c07bb09d', 'eec0eb4f-6444-4f95-aba0-50c24d67f998']],
            allowAutoRemediation: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'CAPLegacyAuth',
            component: CAPLegacyAuth.BaselineComponent,
            fetch_data: [loadConditionalAccessPolicy({ _tenant: tenant })],
            select_data: this.store.pipe(select(client(tenant).graph.conditionalAccessPolicy.all)),
            select_status: this.store.pipe(select(client(tenant).graph.conditionalAccessPolicy.status)),
            canUpdate: true,
            createRemediationAction: CAPLegacyAuth.Remediate,
            remediationActionFailure: [
                createConditionalAccessPolicyFailure,
                updateConditionalAccessPolicyFailure,
                deleteConditionalAccessPolicyFailure,
            ],
            scopes: ['Policy.Read.All', 'Policy.ReadWrite.ConditionalAccess', 'Application.Read.All'],
            href: CAPLegacyAuth.href,
            plans: [['41781fb2-bc02-4b7c-bd55-b576c07bb09d', 'eec0eb4f-6444-4f95-aba0-50c24d67f998']],
            allowAutoRemediation: true,
        }),
        ...[...this.genericCapsTags, ...this.genericCapsTemplateTags].map((tag) => {
            return (tenant: string): TenantBaselineRegistryItem => ({
                tag,
                component: CAPGeneric.BaselineComponent,
                fetch_data: [
                    loadAuthenticationStrengthPolicies({ _tenant: tenant }),
                    loadConditionalAccessPolicy({ _tenant: tenant }),
                    loadDirectoryRoleTemplates({ msp: tenant }),
                    loadNamedLocations({ _tenant: tenant }),
                ],
                select_data: selectCapData(this.store, tenant, tag),
                select_status: selectCapStatus(this.store, tenant),
                meta_data: this.store.pipe(select(client(tenant).graph.users.all)),
                canUpdate: true,
                meta_data_status: this.store.select(client(tenant).graph.users.status),
                createRemediationAction: CAPGeneric.Remediate,
                remediationActionFailure: [
                    createConditionalAccessPolicyFailure,
                    updateConditionalAccessPolicyFailure,
                    deleteConditionalAccessPolicyFailure,
                ],
                scopes: ['Policy.Read.All', 'Policy.ReadWrite.ConditionalAccess', 'Application.Read.All'],
                href: CAPGeneric.href,
                plans: [['41781fb2-bc02-4b7c-bd55-b576c07bb09d', 'eec0eb4f-6444-4f95-aba0-50c24d67f998']],
                allowAutoRemediation: true,
            });
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'SecurityDefaultsPolicy',
            component: SecurityDefaultsPolicy.BaselineComponent,
            fetch_data: [loadSecurityDefaultsPolicy({ _tenant: tenant })],
            select_data: this.store.select(client(tenant).graph.securityDefaults.item),
            select_status: this.store.select(client(tenant).graph.securityDefaults.status),
            createRemediationAction: SecurityDefaultsPolicy.Remediate,
            remediationActionFailure: [updateSecurityDefaultsFailure],
            scopes: ['Policy.Read.All', 'Policy.ReadWrite.ConditionalAccess'],
            href: SecurityDefaultsPolicy.href,
            allowAutoRemediation: false,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'AdminConsentWorkflow',
            component: AdminConsentWorkflow.BaselineComponent,
            fetch_data: [loadAdminConsentRequestPolicy({ _tenant: tenant })],
            select_data: this.store.select(client(tenant).graph.adminConsentRequest.item),
            select_status: this.store.select(client(tenant).graph.adminConsentRequest.status),
            createRemediationAction: AdminConsentWorkflow.Remediate,
            href: AdminConsentWorkflow.href,
            allowAutoRemediation: true,
            canUpdate: true,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'SharepointStorageWarning',
            component: SharepointStorageWarning.BaselineComponent,
            fetch_data: [loadSPOSiteUsage({ _tenant: tenant })],
            select_data: this.store.select(client(tenant).graph.sharepointSiteDetails.all),
            select_status: this.store.select(client(tenant).graph.sharepointSiteDetails.status),
            href: SharepointStorageWarning.href,
            canUpdate: true,
            allowAutoRemediation: false,
        }),
        // intune baselines
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'Windows10CompliancePolicy',
            component: Windows10CompliancePolicy.BaselineComponent,
            select_data: selectCompliancePolicyData(this.store, tenant, 'Windows10CompliancePolicy'),
            fetch_data: [loadCompliancePolicies({ _tenant: tenant })],
            select_status: this.store.pipe(select(client(tenant).graph.intune.compliancePolicies.status)),
            plans: [['c1ec4a95-1f05-45b3-a911-aa3fa01094f5']],
            scopes: ['DeviceManagementConfiguration.ReadWrite.All'],
            allowAutoRemediation: true,
            createRemediationAction: Windows10CompliancePolicy.Remediate,
            remediationActionFailure: [
                createCompliancePolicyFailure,
                updateCompliancePolicyFailure,
                deleteCompliancePolicyFailure,
            ],
            canUpdate: true,
            href: Windows10CompliancePolicy.href,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            tag: 'MacOsCompliancePolicy',
            component: MacOsCompliancePolicy.BaselineComponent,
            select_data: selectCompliancePolicyData(this.store, tenant, 'MacOsCompliancePolicy'),
            fetch_data: [loadCompliancePolicies({ _tenant: tenant })],
            select_status: this.store.pipe(select(client(tenant).graph.intune.compliancePolicies.status)),
            plans: [['c1ec4a95-1f05-45b3-a911-aa3fa01094f5']],
            scopes: ['DeviceManagementConfiguration.ReadWrite.All'],
            allowAutoRemediation: true,
            createRemediationAction: MacOsCompliancePolicy.Remediate,
            remediationActionFailure: [
                createCompliancePolicyFailure,
                updateCompliancePolicyFailure,
                deleteCompliancePolicyFailure,
            ],
            canUpdate: true,
            href: MacOsCompliancePolicy.href,
        }),
        (tenant: string): TenantBaselineRegistryItem => ({
            // Personally-owned work profile
            tag: 'AndroidCompliancePolicy',
            component: AndroidCompliancePolicy.BaselineComponent,
            select_data: selectCompliancePolicyData(this.store, tenant, 'AndroidCompliancePolicy'),
            fetch_data: [loadCompliancePolicies({ _tenant: tenant })],
            select_status: this.store.pipe(select(client(tenant).graph.intune.compliancePolicies.status)),
            plans: [['c1ec4a95-1f05-45b3-a911-aa3fa01094f5']],
            scopes: ['DeviceManagementConfiguration.ReadWrite.All'],
            allowAutoRemediation: true,
            createRemediationAction: AndroidCompliancePolicy.Remediate,
            remediationActionFailure: [
                createCompliancePolicyFailure,
                updateCompliancePolicyFailure,
                deleteCompliancePolicyFailure,
            ],
            canUpdate: true,
            href: AndroidCompliancePolicy.href,
        }),
        (tenant: string) => ({
            tag: 'IOSCompliancePolicy',
            component: IOSCompliancePolicy.BaselineComponent,
            select_data: selectCompliancePolicyData(this.store, tenant, 'IOSCompliancePolicy'),
            fetch_data: [loadCompliancePolicies({ _tenant: tenant })],
            select_status: this.store.pipe(select(client(tenant).graph.intune.compliancePolicies.status)),
            plans: [['c1ec4a95-1f05-45b3-a911-aa3fa01094f5']],
            scopes: ['DeviceManagementConfiguration.ReadWrite.All'],
            allowAutoRemediation: true,
            createRemediationAction: IOSCompliancePolicy.Remediate,
            remediationActionFailure: [
                createCompliancePolicyFailure,
                updateCompliancePolicyFailure,
                deleteCompliancePolicyFailure,
            ],
            canUpdate: true,
            href: IOSCompliancePolicy.href,
        }),
        (tenant: string) => ({
            tag: 'ConfigurationPolicyWindowsEdgeBlockPasswordSaving',
            component: ConfigurationPolicyWindowsEdgeBlockPasswordSaving.BaselineComponent,
            select_data: selectDeviceConfigurationPolicy(
                this.store,
                tenant,
                'ConfigurationPolicyWindowsEdgeBlockPasswordSaving',
            ),
            fetch_data: [loadDeviceConfigurationPolicies({ _tenant: tenant })],
            select_status: this.store.pipe(select(client(tenant).graph.intune.deviceConfigurationPolicy.status)),
            plans: [['c1ec4a95-1f05-45b3-a911-aa3fa01094f5']],
            scopes: ['DeviceManagementConfiguration.ReadWrite.All'],
            allowAutoRemediation: true,
            createRemediationAction: ConfigurationPolicyWindowsEdgeBlockPasswordSaving.Remediate,
            remediationActionFailure: [createCompliancePolicyFailure, updateCompliancePolicyFailure],
            href: ConfigurationPolicyWindowsEdgeBlockPasswordSaving.href,
            canUpdate: true,
        }),
        (tenant: string) => ({
            tag: 'ConfigurationPolicyWindowsChromeBlockPasswordSaving',
            component: ConfigurationPolicyWindowsChromeBlockPasswordSaving.BaselineComponent,
            select_data: selectDeviceConfigurationPolicy(
                this.store,
                tenant,
                'ConfigurationPolicyWindowsChromeBlockPasswordSaving',
            ),
            fetch_data: [loadDeviceConfigurationPolicies({ _tenant: tenant })],
            select_status: this.store.pipe(select(client(tenant).graph.intune.deviceConfigurationPolicy.status)),
            plans: [['c1ec4a95-1f05-45b3-a911-aa3fa01094f5']],
            scopes: ['DeviceManagementConfiguration.ReadWrite.All'],
            allowAutoRemediation: true,
            createRemediationAction: ConfigurationPolicyWindowsChromeBlockPasswordSaving.Remediate,
            remediationActionFailure: [createCompliancePolicyFailure, updateCompliancePolicyFailure],
            href: ConfigurationPolicyWindowsChromeBlockPasswordSaving.href,
            canUpdate: true,
        }),
        (tenant: string) => ({
            tag: 'ConfigurationPolicyWindowsLAPS',
            component: ConfigurationPolicyWindowsLAPS.BaselineComponent,
            select_data: selectDeviceConfigurationPolicy(this.store, tenant, 'ConfigurationPolicyWindowsLAPS'),
            fetch_data: [loadDeviceConfigurationPolicies({ _tenant: tenant })],
            select_status: this.store.pipe(select(client(tenant).graph.intune.deviceConfigurationPolicy.status)),
            plans: [['c1ec4a95-1f05-45b3-a911-aa3fa01094f5']],
            scopes: ['DeviceManagementConfiguration.ReadWrite.All'],
            allowAutoRemediation: true,
            createRemediationAction: ConfigurationPolicyWindowsLAPS.Remediate,
            remediationActionFailure: [createCompliancePolicyFailure, updateCompliancePolicyFailure],
            href: ConfigurationPolicyWindowsLAPS.href,
            canUpdate: true,
        }),
        (tenant: string) => ({
            tag: 'AutoForwardBlockTransportRule',
            component: AutoForwardBlockTransportRule.BaselineComponent,
            select_data: this.store.pipe(
                select(client(tenant).powershell.exoTransportRules.all),
                map((items) =>
                    items.filter(
                        (res) => res.Identity === AutoForwardBlockTransportRule.BlockAutoForwardingIdentityName,
                    ),
                ),
            ),
            meta_data: this.store.pipe(select(client(tenant).powershell.exoTransportRules.all)),
            fetch_data: [loadExoTransportRules({ _tenant: tenant })],
            select_status: this.store.pipe(select(client(tenant).powershell.exoTransportRules.status)),
            meta_data_status: this.store.pipe(select(client(tenant).powershell.exoTransportRules.status)),
            createRemediationAction: AutoForwardBlockTransportRule.Remediate,
            remediationActionFailure: [
                deleteExoTransportRuleFailure,
                createExoTransportRuleFailure,
                updateExoTransportRuleFailure,
            ],
            href: AutoForwardBlockTransportRule.href,
            allowAutoRemediation: true,
        }),
    ];
}
